import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  generalNotesApi, noteSearchApi,
  GeneralNote, NoteSearchHit,
} from '../api/notes';
import { MessageEditor, Markdown } from '../components/RichText';
import Modal from '../components/Modal';
import { Toast, useToast } from '../components/Toast';
import type { RoleType, PageResponse } from '../types';

const NOTES_PER_PAGE = 8;

const creatorBadge = (role?: RoleType | null) => {
  if (!role) return null;
  if (role === 'CUSTOMER') return { className: 'badge badge-customer', label: 'Klient' };
  return { className: 'badge badge-integrator', label: 'Integrator' };
};

const Notes: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('ADMIN');
  const isStaff = isAdmin || user?.roles?.includes('EMPLOYEE_ADMIN') || user?.roles?.includes('EMPLOYEE');

  const [notesPage, setNotesPage] = useState<PageResponse<GeneralNote> | null>(null);
  const [page, setPage] = useState(0);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState('');

  const [query, setQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [hits, setHits] = useState<NoteSearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [savingNew, setSavingNew] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState<GeneralNote | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { toast, showToast, dismissToast } = useToast();

  const loadGeneral = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await generalNotesApi.list(page, NOTES_PER_PAGE);
      setNotesPage(data);
      // Backend przycina numer strony poza zakresem (np. po usunięciu ostatniej na stronie).
      if (data.page !== page) setPage(data.page);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Nie udało się załadować notatek');
    } finally {
      setLoadingList(false);
    }
  }, [page]);

  useEffect(() => { loadGeneral(); }, [loadGeneral]);

  // Super-admin only: refresh the current page quietly while any note is still being indexed.
  const refreshGeneralQuiet = useCallback(async () => {
    try {
      const { data } = await generalNotesApi.list(page, NOTES_PER_PAGE);
      setNotesPage(data);
    } catch { /* ignore transient errors while polling */ }
  }, [page]);
  const notesIndexing = !!isAdmin && (notesPage?.content?.some((n) => n.embeddingPending) ?? false);
  useEffect(() => {
    if (!notesIndexing) return;
    // Nie odpytujemy ukrytej karty; twardy limit ~2 min na wypadek, gdyby
    // indeksacja utknęła i embeddingPending nigdy nie zgasło.
    let polls = 0;
    const t = setInterval(() => {
      if (document.hidden) return;
      if (++polls > 48) { clearInterval(t); return; }
      refreshGeneralQuiet();
    }, 2500);
    return () => clearInterval(t);
  }, [notesIndexing, refreshGeneralQuiet]);

  // Search runs only on demand (button / Enter) — no live search while typing.
  const runSearch = useCallback(async (raw: string) => {
    const q = raw.trim();
    if (!q) {
      setHits(null);
      setActiveQuery('');
      setSearching(false);
      setSummary(null);
      setSummaryLoading(false);
      return;
    }
    setSearching(true);
    setSummary(null);
    setSummaryLoading(true);
    // Summary is generated in parallel so the hits list isn't blocked by the LLM call.
    noteSearchApi.summarize(q)
      .then(({ data }) => setSummary(data.summary))
      .catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false));
    try {
      const { data } = await noteSearchApi.search(q);
      setHits(data);
      setActiveQuery(q);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Wyszukiwanie nie powiodło się');
      setHits([]);
      setActiveQuery(q);
    } finally {
      setSearching(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setQuery('');
    setHits(null);
    setActiveQuery('');
    setSearching(false);
    setSummary(null);
    setSummaryLoading(false);
  }, []);

  const grouped = useMemo(() => {
    if (!hits) return { machine: [] as NoteSearchHit[], general: [] as NoteSearchHit[] };
    return {
      machine: hits.filter((h) => h.type === 'MACHINE'),
      general: hits.filter((h) => h.type === 'GENERAL'),
    };
  }, [hits]);

  if (!user) return <Navigate to="/login" replace />;
  if (!isStaff) return <Navigate to="/machines" replace />;

  const addNote = async () => {
    if (!newContent.trim()) return;
    setSavingNew(true);
    setError('');
    try {
      await generalNotesApi.create(newContent.trim());
      setNewContent('');
      setShowAdd(false);
      // Nowa notatka jest najnowsza → skacz na pierwszą stronę. Gdy już na niej jesteśmy,
      // setPage(0) nie odpali efektu, więc odświeżamy ręcznie.
      if (page === 0) await loadGeneral(); else setPage(0);
      showToast('success', 'Notatka dodana.');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Nie udało się zapisać notatki';
      setError(msg);
      showToast('error', msg);
    } finally {
      setSavingNew(false);
    }
  };

  const startEdit = (n: GeneralNote) => {
    setEditingId(n.id);
    setEditContent(n.content);
  };

  const saveEdit = async () => {
    if (!editingId || !editContent.trim()) return;
    setSavingEdit(true);
    try {
      await generalNotesApi.update(editingId, editContent.trim());
      setEditingId(null);
      setEditContent('');
      await loadGeneral();
      showToast('success', 'Notatka zaktualizowana.');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Nie udało się zaktualizować notatki';
      setError(msg);
      showToast('error', msg);
    } finally {
      setSavingEdit(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await generalNotesApi.delete(confirmDelete.id);
      setConfirmDelete(null);
      await loadGeneral();
      showToast('success', 'Notatka usunięta.');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Nie udało się usunąć notatki';
      setError(msg);
      showToast('error', msg);
    } finally {
      setDeleting(false);
    }
  };

  const canEdit = (n: GeneralNote) =>
    !!isAdmin || n.createdByUserId === user.userId;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h1 className="page-title" style={{ marginBottom: 0 }}>Notatki</h1>
        {!showAdd && (
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
            Dodaj notatkę
          </button>
        )}
      </div>

      {error && <div className="auth-error">{error}</div>}

      {user?.plan !== 'PRO' && user?.plan !== 'FREE' && !user?.roles?.includes('ADMIN') ? (
        <div className="card" style={{ padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
          <div className="eyebrow" style={{ marginBottom: '0.375rem' }}>Wyszukiwarka notatek</div>
          <div className="item-meta">
            Wyszukiwanie semantyczne po notatkach jest dostępne w planie PRO.
            Notatki możesz nadal przeglądać i dodawać poniżej.
          </div>
        </div>
      ) : (
      <div className="card" style={{ padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
        <textarea
          className="form-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          rows={3}
          autoFocus
          style={{
            width: '100%', resize: 'vertical', minHeight: '4.5rem', lineHeight: 1.5,
            border: '2px solid var(--color-primary)',
            boxShadow: '0 0 0 3px color-mix(in srgb, var(--color-primary) 15%, transparent)',
          }}
        />
        <div className="item-meta" style={{ textAlign: 'center', marginTop: '0.5rem' }}>
          Opisz co chcesz znaleźć w notatkach?
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => runSearch(query)}
            disabled={!query.trim() || searching}
          >
            {searching ? 'Szukam…' : 'Szukaj'}
          </button>
          {(activeQuery || query) && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={clearSearch}
              disabled={searching}
            >
              Wyczyść
            </button>
          )}
        </div>
      </div>
      )}

      {(summaryLoading || summary !== null) && (
        <div className="card" style={{ padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
          <div className="eyebrow" style={{ marginBottom: '0.375rem' }}>Podsumowanie</div>
          {summaryLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--color-text-secondary)' }}>
              <span className="ai-spinner" aria-hidden="true" />
              <span>Podsumowuję wyniki…</span>
            </div>
          ) : (
            <p style={{ margin: 0, lineHeight: 1.6 }}>{summary}</p>
          )}
        </div>
      )}

      {showAdd && (
        <Modal
          title="Dodaj notatkę"
          onClose={() => { setShowAdd(false); setNewContent(''); }}
          dismissable={!savingNew}
        >
          <div className="form-group">
            <textarea
              className="form-input"
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              rows={5}
              autoFocus
              placeholder="Treść notatki…"
              style={{ width: '100%', resize: 'vertical', minHeight: '6rem', lineHeight: 1.5 }}
            />
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setShowAdd(false); setNewContent(''); }}
              disabled={savingNew}
            >
              Anuluj
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={addNote}
              disabled={savingNew || !newContent.trim()}
            >
              {savingNew ? 'Zapisuję…' : 'Zapisz'}
            </button>
          </div>
        </Modal>
      )}

      {/* SEARCH MODE */}
      {activeQuery && hits !== null && (
        <>
          <div className="eyebrow" style={{ marginBottom: '0.5rem' }}>
            Wyniki dla: „{activeQuery}"
            {searching && <span style={{ marginLeft: '0.5rem', textTransform: 'none', color: 'var(--color-text-muted)' }}>(odświeżanie…)</span>}
          </div>

          {grouped.machine.length === 0 && grouped.general.length === 0 ? (
            <div className="empty-state">Brak trafień. Spróbuj inaczej sformułować pytanie.</div>
          ) : (
            <>
              {grouped.machine.length > 0 && (
                <section style={{ marginBottom: '1.5rem' }}>
                  <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
                    Notatki maszyn <span className="item-meta" style={{ display: 'inline', fontSize: '0.8125rem' }}>({grouped.machine.length})</span>
                  </h2>
                  <div className="items-list">
                    {grouped.machine.map((h) => (
                      <article key={h.noteId} className="item-card card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          {h.machineId ? (
                            <Link to={`/machines/${h.machineId}?tab=notes`} className="badge badge-primary" style={{ textDecoration: 'none' }}>
                              {h.machineName || 'Maszyna'}
                            </Link>
                          ) : (
                            <span className="badge badge-primary">Maszyna</span>
                          )}
                          <ScoreBadge score={h.score} />
                        </div>
                        <p className="item-desc">{h.snippet}</p>
                        <FooterLine creator={h.creator} when={h.createdAt} />
                      </article>
                    ))}
                  </div>
                </section>
              )}

              {grouped.general.length > 0 && (
                <section style={{ marginBottom: '1.5rem' }}>
                  <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
                    Notatki ogólne <span className="item-meta" style={{ display: 'inline', fontSize: '0.8125rem' }}>({grouped.general.length})</span>
                  </h2>
                  <div className="items-list">
                    {grouped.general.map((h) => (
                      <article key={h.noteId} className="item-card card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <span className="badge badge-employee">Ogólna</span>
                          <ScoreBadge score={h.score} />
                        </div>
                        <p className="item-desc">{h.snippet}</p>
                        <FooterLine creator={h.creator} when={h.updatedAt || h.createdAt} />
                      </article>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </>
      )}

      {/* BROWSE MODE — recent general notes */}
      {!activeQuery && (
        <section>
          <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Notatki ogólne</h2>
          {loadingList ? (
            <div className="empty-state">Ładowanie…</div>
          ) : (notesPage?.content?.length ?? 0) === 0 ? (
            <div className="empty-state">Brak notatek ogólnych. Kliknij „Dodaj notatkę" żeby zacząć.</div>
          ) : (
            <>
            <div className="items-list">
              {notesPage!.content.map((n) => (
                <article key={n.id} className="item-card card">
                  {editingId === n.id ? (
                    <>
                      <MessageEditor value={editContent} onChange={setEditContent} rows={5} />
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                        <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={savingEdit || !editContent.trim()}>
                          {savingEdit ? 'Zapisuję…' : 'Zapisz'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)} disabled={savingEdit}>
                          Anuluj
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="item-desc"><Markdown content={n.content} /></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <FooterLine
                          creator={{
                            firstName: n.createdByFirstName,
                            lastName: n.createdByLastName,
                            role: n.createdByRole,
                          }}
                          when={n.updatedAt}
                        />
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
                          {isAdmin && n.embeddingPending && <span className="index-badge">Indeksowanie…</span>}
                          {canEdit(n) && (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button className="btn btn-primary btn-sm" onClick={() => startEdit(n)}>Edytuj</button>
                              <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(n)}>Usuń</button>
                            </div>
                          )}
                        </span>
                      </div>
                    </>
                  )}
                </article>
              ))}
            </div>
            {notesPage!.totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
                <button className="btn btn-secondary btn-sm" disabled={notesPage!.page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                  Poprzednia
                </button>
                <span className="item-meta pagination-info">Strona {notesPage!.page + 1} z {notesPage!.totalPages}</span>
                <button className="btn btn-secondary btn-sm" disabled={notesPage!.page >= notesPage!.totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                  Następna
                </button>
              </div>
            )}
            </>
          )}
        </section>
      )}

      {confirmDelete && (
        <Modal
          title="Usuń notatkę"
          onClose={() => { if (!deleting) setConfirmDelete(null); }}
          dismissable={!deleting}
        >
          <p style={{ marginBottom: '0.75rem', lineHeight: 1.6 }}>
            Czy na pewno chcesz usunąć tę notatkę? Tej operacji nie można cofnąć.
          </p>
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setConfirmDelete(null)}
              disabled={deleting}
            >
              Anuluj
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={doDelete}
              disabled={deleting}
            >
              {deleting ? 'Usuwam…' : 'Usuń'}
            </button>
          </div>
        </Modal>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

const ScoreBadge: React.FC<{ score: number }> = ({ score }) => {
  const pct = Math.round(score * 100);
  return (
    <span className="item-meta" style={{ fontSize: '0.6875rem' }} title="Podobieństwo semantyczne">
      trafność {pct}%
    </span>
  );
};

const FooterLine: React.FC<{
  creator?: { firstName?: string | null; lastName?: string | null; role?: RoleType | null } | null;
  when?: string;
}> = ({ creator, when }) => {
  const badge = creatorBadge(creator?.role);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
      {when && <span className="item-meta">{new Date(when).toLocaleString('pl-PL')}</span>}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
        {creator?.firstName && (
          <span className="item-meta">{creator.firstName} {creator.lastName}</span>
        )}
        {badge && <span className={badge.className}>{badge.label}</span>}
      </span>
    </div>
  );
};

export default Notes;
